import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField, min, minLength, required } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import {
  ContactDetector,
  PropertyKeyManagement,
  PropertyMandateDto,
  PropertyMandateDuration,
  PropertyMandateType,
  PropertySellerPriority,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AlertAuraComponent, AuraColorType } from '@workspace/ng-aura';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';

export interface PropertyMandateModel {
  type: PropertyMandateType | null;
  duration: PropertyMandateDuration;
  targetCommissionRate: number | null;
  keyManagement: PropertyKeyManagement[];
  priorities: PropertySellerPriority[];
  specialInstructions: string;
}

export interface MandateTypeOption {
  value: PropertyMandateType;
  title: string;
  badge?: string;
  badgeColorClass?: string;
  description: string;
  benefits: string[];
  drawbacks?: string[];
}

export interface MandateDurationOption {
  value: PropertyMandateDuration;
  label: string;
  hint: string;
}

export interface SellerPriorityOption {
  value: PropertySellerPriority;
  icon: string;
  label: string;
  description: string;
}

export interface KeyManagementOption {
  value: PropertyKeyManagement;
  icon: string;
  label: string;
  badge?: string;
  description: string;
}

export interface MandateCommissionConfig {
  min: number;
  max: number;
  defaultRate: number;
  recommendedRangeText: string;
  minLabel: string;
  maxLabel: string;
  adviceHint: string;
}

export const MANDATE_COMMISSION_CONFIG: Record<PropertyMandateType, MandateCommissionConfig> = {
  [PropertyMandateType.EXCLUSIVE]: {
    min: 2.0,
    max: 5.5,
    defaultRate: 3.5,
    recommendedRangeText: '2.8 % à 3.8 %',
    minLabel: '2.0 %',
    maxLabel: '5.5 %',
    adviceHint:
      "En mandat exclusif, l'agent est assuré de percevoir ses honoraires s'il vend le bien : vous pouvez légitimement cibler un taux d'honoraires préférentiel.",
  },
  [PropertyMandateType.SEMI_EXCLUSIVE]: {
    min: 2.5,
    max: 6.0,
    defaultRate: 4.0,
    recommendedRangeText: '3.2 % à 4.5 %',
    minLabel: '2.5 %',
    maxLabel: '6.0 %',
    adviceHint:
      "En semi-exclusif, un seul agent pro vous représente tout en vous laissant la liberté de trouver un acquéreur en direct. C'est la formule équilibrée recommandée.",
  },
  [PropertyMandateType.SIMPLE]: {
    min: 3.5,
    max: 8.0,
    defaultRate: 5.5,
    recommendedRangeText: '4.8 % à 6.0 %',
    minLabel: '3.5 %',
    maxLabel: '8.0 %',
    adviceHint:
      "En mandat simple, l'agent investit du temps avec une concurrence forte et un risque d'échec élevé : les barèmes d'honoraires appliqués sont plus élevés.",
  },
};

@Component({
  selector: 'etxe-property-mandate-form',
  standalone: true,
  imports: [
    CommonModule,
    FormField,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSliderModule,
    AlertAuraComponent,
  ],
  templateUrl: './mandate-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyMandateFormComponent extends AbstractSignalFormComponent<
  PropertyMandateDto,
  PropertyMandateModel
> {
  sellingPriceCents = input<number | undefined>();
  auraColorType = AuraColorType;

  PropertyMandateType = PropertyMandateType;
  PropertyMandateDuration = PropertyMandateDuration;
  PropertySellerPriority = PropertySellerPriority;
  PropertyKeyManagement = PropertyKeyManagement;

  override model: WritableSignal<PropertyMandateModel> = signal<PropertyMandateModel>({
    type: null,
    duration: PropertyMandateDuration.THREE_MONTHS,
    targetCommissionRate: 3.5,
    keyManagement: [],
    priorities: [],
    specialInstructions: '',
  });

  override formTree = form(this.model, (b) => {
    required(b.type);
    required(b.duration);
    required(b.targetCommissionRate);
    min(b.targetCommissionRate, 0.5);
    required(b.keyManagement);
    minLength(b.keyManagement, 1);
    required(b.priorities);
    minLength(b.priorities, 1);
  });

  commissionConfig = computed<MandateCommissionConfig>(() => {
    const type = this.model().type;
    if (type && MANDATE_COMMISSION_CONFIG[type]) {
      return MANDATE_COMMISSION_CONFIG[type];
    }
    return {
      min: 2.0,
      max: 8.0,
      defaultRate: 3.5,
      recommendedRangeText: '3.0 % à 5.0 % (Moyenne générale)',
      minLabel: '2.0 %',
      maxLabel: '8.0 %',
      adviceHint:
        "Sélectionnez un type de mandat ci-dessus pour ajuster précisément la fourchette d'honoraires recommandée.",
    };
  });

  mandateTypes: MandateTypeOption[] = [
    {
      value: PropertyMandateType.SEMI_EXCLUSIVE,
      title: 'Mandat semi-exclusif',
      badge: 'Recommandé ETXE',
      badgeColorClass: 'bg-primary/10 text-primary border-primary/30',
      description:
        'Un seul agent représente le vendeur pour maximiser son investissement, tout en lui conservant la liberté de trouver lui-même un acquéreur.',
      benefits: ['Un seul interlocuteur, agent très motivé et investi', 'Liberté de vente directe maintenue'],
    },
    {
      value: PropertyMandateType.EXCLUSIVE,
      title: 'Mandat exclusif',
      badge: 'Impact maximal',
      badgeColorClass: 'bg-amber-a3 text-amber-11 border-amber-a5',
      description:
        "L'agent bénéficie de l'exclusivité totale. En contrepartie, le vendeur bénéficie du plus haut niveau d'engagement marketing et d'un taux d'honoraires préférentiel.",
      benefits: [
        'Un seul interlocuteur, agent très motivé et investi',
        'Délégation totale et déploiement marketing complet',
        'Honoraires négociés à la baisse',
      ],
    },
    {
      value: PropertyMandateType.SIMPLE,
      title: 'Mandat simple',
      description: 'Le vendeur peut mandater plusieurs agences simultanément et vendre par lui-même.',
      benefits: [
        'Liberté contractuelle totale',
        'Possibilité de vente directe par vous-même',
        'Multi-diffusion par plusieurs agences',
      ],
      drawbacks: [
        'Commission généralement plus élevée',
        'Nombreuses visites sur rendez-vous en votre présence',
        'Davantage sollicité',
      ],
    },
  ];

  durations: MandateDurationOption[] = [
    {
      value: PropertyMandateDuration.TWO_MONTHS,
      label: '2 mois',
      hint: 'Vente accélérée',
    },
    {
      value: PropertyMandateDuration.THREE_MONTHS,
      label: '3 mois',
      hint: 'Standard du marché',
    },
    {
      value: PropertyMandateDuration.SIX_MONTHS,
      label: '6 mois',
      hint: 'Bien spécifique ou rural',
    },
    {
      value: PropertyMandateDuration.FLEXIBLE,
      label: 'Flexible',
      hint: 'À convenir avec l’agent',
    },
  ];

  priorityOptions: SellerPriorityOption[] = [
    {
      value: PropertySellerPriority.QUALIFIED_BUYERS,
      icon: 'verified_user',
      label: 'Acquéreurs qualifiés',
      description: 'Vérification de la solvabilité bancaire avant toute visite',
    },
    {
      value: PropertySellerPriority.PREMIUM_VISUALS,
      icon: 'photo_camera',
      label: 'Mise en valeur premium',
      description: 'Photos professionnelles HDR, visite 3D immersive ou vidéo',
    },
    {
      value: PropertySellerPriority.DAILY_REPORTS,
      icon: 'chat',
      label: 'Comptes-rendus réactifs',
      description: 'Retour écrit et debrief après chaque visite sous 24-48h',
    },
    {
      value: PropertySellerPriority.SPEED,
      icon: 'bolt',
      label: 'Vente rapide',
      description: 'Priorité au délai de transaction et réactivité',
    },
    {
      value: PropertySellerPriority.MAX_PRICE,
      icon: 'payments',
      label: 'Meilleur prix',
      description: 'Défense ferme de la valeur du bien sans braderie',
    },
    {
      value: PropertySellerPriority.VALUATION,
      icon: 'assessment',
      label: 'Estimation du bien',
      description: 'Avis de valeur approfondi et analyse comparative du marché par l’agent',
    },
  ];

  keyManagementOptions: KeyManagementOption[] = [
    {
      value: PropertyKeyManagement.AGENT_KEY_DOUBLE,
      icon: 'key',
      label: "Double des clés remis à l'agent",
      badge: 'Recommandé',
      description:
        "Visites en autonomie complète avec récépissé de remise de clés (responsabilité couverte par l'assurance pro de l'agent).",
    },
    {
      value: PropertyKeyManagement.KEY_BOX,
      icon: 'pin',
      label: 'Boîte à clés sécurisée à code',
      description:
        'Idéal si le bien est vacant ou en résidence secondaire. Code sécurisé transmis à chaque visite validée.',
    },
    {
      value: PropertyKeyManagement.BY_APPOINTMENT_OWNER,
      icon: 'event_available',
      label: 'Sur rendez-vous en votre présence',
      description:
        'Vous conservez vos clés. L’agent planifie chaque visite selon vos disponibilités et vous ouvrez vous-même la porte.',
    },
  ];

  hasContactInInstructions = computed(() => {
    const text = this.model().specialInstructions || '';
    return ContactDetector.detect(text).hasContact;
  });

  sellingPriceEuros = computed(() => {
    const priceCents = this.sellingPriceCents();
    if (!priceCents) return null;
    return priceCents / 100;
  });

  estimatedCommissionEuros = computed(() => {
    const price = this.sellingPriceEuros();
    const rate = this.model().targetCommissionRate;
    if (!price || !rate || rate <= 0) return null;
    return Math.round((price * rate) / 100);
  });

  selectMandateType(type: PropertyMandateType) {
    const config =
      MANDATE_COMMISSION_CONFIG[type] || MANDATE_COMMISSION_CONFIG[PropertyMandateType.SEMI_EXCLUSIVE];

    // En mandat simple, les visites se font obligatoirement sur RDV en présence du propriétaire
    let keyManagement = this.model().keyManagement;
    if (type === PropertyMandateType.SIMPLE) {
      keyManagement = [PropertyKeyManagement.BY_APPOINTMENT_OWNER];
    }

    this.model.update((m) => ({
      ...m,
      type,
      targetCommissionRate: config.defaultRate,
      keyManagement,
    }));
  }

  selectDuration(duration: PropertyMandateDuration) {
    this.model.update((m) => ({ ...m, duration }));
  }

  toggleKeyManagement(keyManagement: PropertyKeyManagement) {
    this.model.update((m) => {
      const exists = m.keyManagement.includes(keyManagement);
      const updated = exists
        ? m.keyManagement.filter((k) => k !== keyManagement)
        : [...m.keyManagement, keyManagement];
      return { ...m, keyManagement: updated };
    });
  }

  isKeyManagementSelected(keyManagement: PropertyKeyManagement): boolean {
    return this.model().keyManagement.includes(keyManagement);
  }

  togglePriority(priority: PropertySellerPriority) {
    this.model.update((m) => {
      const exists = m.priorities.includes(priority);
      const updated = exists ? m.priorities.filter((p) => p !== priority) : [...m.priorities, priority];
      return { ...m, priorities: updated };
    });
  }

  isPrioritySelected(priority: PropertySellerPriority): boolean {
    return this.model().priorities.includes(priority);
  }

  updateCommissionRate(value: number | null) {
    this.model.update((m) => ({ ...m, targetCommissionRate: value }));
  }

  protected override buildModelFromDto(dto: PropertyMandateDto): PropertyMandateModel {
    const type = dto?.type ?? null;
    const config = type ? MANDATE_COMMISSION_CONFIG[type] : null;

    let keyManagement: PropertyKeyManagement[] = [];
    if (Array.isArray(dto?.keyManagement)) {
      keyManagement = dto.keyManagement;
    } else if (dto?.keyManagement) {
      keyManagement = [dto.keyManagement as unknown as PropertyKeyManagement];
    }

    if (type === PropertyMandateType.SIMPLE) {
      keyManagement = [PropertyKeyManagement.BY_APPOINTMENT_OWNER];
    }

    return {
      type,
      duration: dto?.duration || PropertyMandateDuration.THREE_MONTHS,
      targetCommissionRate: dto?.targetCommissionRate ?? config?.defaultRate ?? 3.5,
      keyManagement,
      priorities: dto?.priorities || [],
      specialInstructions: dto?.specialInstructions || '',
    };
  }

  protected override buildDtoFromModel(model: PropertyMandateModel): PropertyMandateDto {
    const rawDto: PropertyMandateDto = {
      ...(this.dto() || ({} as PropertyMandateDto)),
      type: model.type ?? undefined,
      duration: model.duration,
      targetCommissionRate: model.targetCommissionRate ?? undefined,
      keyManagement: model.keyManagement || [],
      priorities: model.priorities || [],
      specialInstructions: model.specialInstructions || undefined,
    };

    return PropertyDataCleaner.deepClean(rawDto) as PropertyMandateDto;
  }
}
